import React, { useEffect, useRef, useState } from 'react';
import { Calendar, X } from 'lucide-react';
import PropTypes from 'prop-types';
import useDropdownPosition from '../../../hooks/useDropdownPosition';
import { formatDateWithOptions } from '../../../utils/dateUtils';

const DateRangeDropdown = ({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onClear,
  isOpen,
  onClose,
  buttonRef
}) => {
  const dropdownRef = useRef(null);

  // Use smart positioning hook
  const { position } = useDropdownPosition(isOpen, 320, buttonRef);

  // Check if we're on a narrow screen (mobile)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is on a filter button - if so, don't close
      const isFilterButton = event.target.closest('[data-filter-button]');
      if (isFilterButton) {
        return;
      }

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, dropdownRef]);
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayString = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Helper function to get date string N days ago
  const getDaysAgoString = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  };

  // Helper function to get start of year
  const getStartOfYearString = () => {
    const date = new Date();
    date.setMonth(0, 1);
    return date.toISOString().split('T')[0];
  };

  // Helper function to get start of last year
  const getStartOfLastYearString = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    date.setMonth(0, 1);
    return date.toISOString().split('T')[0];
  };

  // Preset date ranges
  const presets = [
    {
      label: 'Last 7 days',
      from: getDaysAgoString(7),
      to: getTodayString()
    },
    {
      label: 'Last 30 days',
      from: getDaysAgoString(30),
      to: getTodayString()
    },
    {
      label: 'Last 90 days',
      from: getDaysAgoString(90),
      to: getTodayString()
    },
    {
      label: 'This year',
      from: getStartOfYearString(),
      to: getTodayString()
    },
    {
      label: 'Last year',
      from: getStartOfLastYearString(),
      to: getStartOfYearString()
    }
  ];

  const handlePresetSelect = (preset) => {
    onDateFromChange(preset.from);
    onDateToChange(preset.to);
    onClose();
  };

  const hasActiveFilter = dateFrom || dateTo;

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={onClose}
        />
      )}

      <div
        ref={dropdownRef}
        className={`${
          isMobile
            ? 'fixed inset-x-4 top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto'
            : 'absolute top-full mt-1 w-80'
        } bg-white border border-gray-200 rounded-lg shadow-lg z-50`}
        style={
          isMobile
            ? {
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '280px'
              }
            : {
                left: position.left,
                right: position.right,
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '320px'
              }
        }
      >
        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-medium text-gray-900">Filing Date</h3>
            </div>
            <div className="flex items-center gap-2">
              {hasActiveFilter && (
                <button
                  onClick={onClear}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100"
                >
                  <X className="w-3 h-3" />
                  Clear
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Preset Buttons */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">
              Quick Select
            </label>
            <div className="grid grid-cols-2 gap-2">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handlePresetSelect(preset)}
                  className="px-3 py-2 text-xs bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors text-left"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range */}
          <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-700">
              Custom Range
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* From Date */}
              <div className="space-y-1">
                <label className="block text-xs text-gray-600">From</label>
                <input
                  type="date"
                  value={dateFrom || ''}
                  onChange={(e) => onDateFromChange(e.target.value || null)}
                  max={dateTo || getTodayString()}
                  className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* To Date */}
              <div className="space-y-1">
                <label className="block text-xs text-gray-600">To</label>
                <input
                  type="date"
                  value={dateTo || ''}
                  onChange={(e) => onDateToChange(e.target.value || null)}
                  min={dateFrom || undefined}
                  max={getTodayString()}
                  className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Active Filter Display */}
          {hasActiveFilter && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-800">
                {dateFrom && dateTo
                  ? `${formatDateWithOptions(dateFrom, { month: 'short', day: 'numeric', year: 'numeric' })} - ${formatDateWithOptions(dateTo, { month: 'short', day: 'numeric', year: 'numeric' })}`
                  : dateFrom
                    ? `From ${formatDateWithOptions(dateFrom, { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : `Until ${formatDateWithOptions(dateTo, { month: 'short', day: 'numeric', year: 'numeric' })}`}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

DateRangeDropdown.propTypes = {
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string,
  onDateFromChange: PropTypes.func.isRequired,
  onDateToChange: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  buttonRef: PropTypes.object
};

export default DateRangeDropdown;
